import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { APP_ENV, isBugLoggingEnabled } from '@/lib/env';
import { scrubRecord, scrubText, stripUrl } from '@/lib/privacyScrub';
import { Sentry } from '@/lib/sentry';

export type ErrorSeverity = 'error' | 'warning' | 'critical';

/**
 * The most an error_logs row may hold. The insert policy refuses anything
 * larger (20270449060000_bound_anonymous_log_writes.sql), so the logger clips
 * to these first rather than lose the row. Metadata is measured as
 * JSON.stringify bytes against half the server's 8 KB, because Postgres
 * prints jsonb with extra spaces.
 */
export const ERROR_LOG_LIMITS = {
  message: 2000,
  stack: 8000,
  name: 200,
  userAgent: 512,
  url: 2048,
  metadataBytes: 4096,
} as const;

const clip = (value: string, max: number): string => (value.length > max ? value.slice(0, max) : value);

function boundedMetadata<T extends { environment: string }>(metadata: T) {
  const bytes = new TextEncoder().encode(JSON.stringify(metadata)).length;
  if (bytes <= ERROR_LOG_LIMITS.metadataBytes) return metadata;
  return { environment: metadata.environment, metadata_truncated: true, metadata_bytes: bytes };
}

interface LogErrorOptions {
  componentName?: string;
  actionName?: string;
  errorCode?: string;
  metadata?: Record<string, unknown>;
  severity?: ErrorSeverity;
}

/**
 * Log an error to the error_logs table.
 * - Staging / isBugLoggingEnabled: verbose console.group output + DB write.
 * - Production: DB write only (minimal console noise).
 */
export async function logError(
  error: Error | string,
  options: LogErrorOptions = {}
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    // The message and stack are free text: a server or validation error may
    // echo what the user submitted (M11).
    const errorMessage = scrubText(typeof error === 'string' ? error : error.message);
    const errorStack = error instanceof Error && error.stack ? scrubText(error.stack) : undefined;
    // M11: personal data never leaves in diagnostics, whatever the caller
    // passed (src/lib/privacyScrub.ts).
    const metadata = scrubRecord(options.metadata);

    // No-op when Sentry isn't initialized (dev/demo/UAT, or no DSN configured).
    Sentry.captureException(error instanceof Error ? error : new Error(errorMessage), {
      level: options.severity === 'critical' ? 'fatal' : options.severity,
      tags: {
        componentName: options.componentName,
        actionName: options.actionName,
        errorCode: options.errorCode,
      },
      extra: metadata,
    });

    // Verbose console output when bug logging is enabled (staging)
    /* eslint-disable no-console -- intentional staging bug-log group, gated behind isBugLoggingEnabled */
    if (isBugLoggingEnabled) {
      console.group('[BUG LOG]');
      console.error('Message:', errorMessage);
      if (errorStack) console.error('Stack:', errorStack);
      if (options.componentName) console.info('Component:', options.componentName);
      if (options.actionName) console.info('Action:', options.actionName);
      if (options.errorCode) console.info('Code:', options.errorCode);
      if (metadata) console.info('Metadata:', metadata);
      console.groupEnd();
    }
    /* eslint-enable no-console */

    // Merge environment into metadata so errors are filterable by env
    const enrichedMetadata = {
      ...(metadata || {}),
      environment: APP_ENV,
    };

    await supabase
      .from('error_logs')
      .insert({
        user_id: user?.id || null,
        error_message: clip(errorMessage, ERROR_LOG_LIMITS.message),
        error_stack: errorStack === undefined ? undefined : clip(errorStack, ERROR_LOG_LIMITS.stack),
        error_code: options.errorCode ? clip(options.errorCode, ERROR_LOG_LIMITS.name) : null,
        component_name: options.componentName ? clip(options.componentName, ERROR_LOG_LIMITS.name) : null,
        action_name: options.actionName ? clip(options.actionName, ERROR_LOG_LIMITS.name) : null,
        metadata: boundedMetadata(enrichedMetadata),
        user_agent: clip(navigator.userAgent, ERROR_LOG_LIMITS.userAgent),
        url: clip(stripUrl(window.location.href), ERROR_LOG_LIMITS.url),
        severity: options.severity || 'error',
      });
  } catch (trackingError) {
    // Fail silently to avoid infinite loops
    console.error('Failed to track error:', trackingError);
  }
}

/**
 * Write a structured bug report to the bug_reports table.
 * No-op in production (only active when isBugLoggingEnabled is true).
 */
export async function logBugReport(
  description: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  if (!isBugLoggingEnabled) return;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    await (supabase
      .from('bug_reports')
      .insert({
        description: scrubText(description),
        metadata: (scrubRecord(metadata) ?? {}) as Json,
        environment: APP_ENV,
        url: stripUrl(window.location.href),
        user_agent: navigator.userAgent,
        user_id: user?.id ?? null,
      }));
  } catch (err) {
    console.error('[BUG LOG] Failed to write bug report:', err);
  }
}

/**
 * Create an error boundary logger scoped to a component name.
 */
export function createErrorLogger(componentName: string) {
  return {
    logError: (error: Error, errorInfo?: Record<string, unknown>) => {
      logError(error, {
        componentName,
        metadata: errorInfo,
        severity: 'error',
      });
    },
    logWarning: (message: string, metadata?: Record<string, unknown>) => {
      logError(message, {
        componentName,
        metadata,
        severity: 'warning',
      });
    },
    logCritical: (error: Error, metadata?: Record<string, unknown>) => {
      logError(error, {
        componentName,
        metadata,
        severity: 'critical',
      });
    },
  };
}

/**
 * Get recent errors summary.
 */
export async function getRecentErrorsSummary() {
  try {
    const { data, error } = await supabase
      .from('recent_errors_summary')
      .select('severity, component_name, error_count, last_occurred, affected_users, resolved_count');

    if (error) {
      console.error('Failed to get errors summary:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error getting errors summary:', error);
    return [];
  }
}

/**
 * Get detailed error logs with optional filters.
 */
export async function getErrorLogs(filters?: {
  severity?: ErrorSeverity;
  component?: string;
  resolved?: boolean;
  limit?: number;
}) {
  try {
    let query = supabase
      .from('error_logs')
      .select('id, user_id, error_message, error_stack, error_code, component_name, action_name, metadata, user_agent, url, severity, resolved, resolved_at, resolved_by, created_at')
      .order('created_at', { ascending: false });

    if (filters?.severity) {
      query = query.eq('severity', filters.severity);
    }
    if (filters?.component) {
      query = query.eq('component_name', filters.component);
    }
    if (filters?.resolved !== undefined) {
      query = query.eq('resolved', filters.resolved);
    }
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Failed to get error logs:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error getting error logs:', error);
    return [];
  }
}

/**
 * Mark an error as resolved.
 */
export async function resolveError(errorId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    const { error } = await supabase
      .from('error_logs')
      .update({
        resolved: true,
        resolved_at: new Date().toISOString(),
        resolved_by: user?.id,
      })
      .eq('id', errorId);

    if (error) {
      console.error('Failed to resolve error:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error resolving error:', error);
    return false;
  }
}

/**
 * A browser notice that is not an error: ResizeObserver reports a layout loop
 * it already resolved. Logged as critical it filled 55 of 58 critical rows in
 * October 2026 and would make the health monitor cry wolf.
 */
export function isBenignBrowserNotice(message: unknown): boolean {
  return typeof message === 'string' && /^ResizeObserver loop/i.test(message.trim());
}

/**
 * Install global unhandled-error listeners.
 * Only active when isBugLoggingEnabled is true (staging).
 * In production, errors are logged via explicit logError() calls only.
 */
export function setupGlobalErrorHandler(): void {
  if (!isBugLoggingEnabled) return;

  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason ?? 'Unhandled rejection'));
    logError(err, {
      componentName: 'Global',
      actionName: 'unhandledRejection',
      severity: 'critical',
    });
  });

  window.addEventListener('error', (event) => {
    if (!event.error && !event.message) return;
    if (isBenignBrowserNotice(event.message)) return;
    logError(event.error instanceof Error ? event.error : event.message, {
      componentName: 'Global',
      actionName: 'globalError',
      severity: 'critical',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
}
